import { useRef, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Popover, PopoverButton, PopoverPanel, Portal } from '@headlessui/react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import type { RoastExtraColumnKey, RoastProfileRow } from './types';
import { extraColumnOptions } from './RoastProfileUtils';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastProfileTableToolbarProps {
  rows: RoastProfileRow[];
  readOnly: boolean;
  extraColumns: RoastExtraColumnKey[];
  onExtraColumnsChange: (value: RoastExtraColumnKey[]) => void;
  onAddManualRow?: (timeStr: string) => void;
}

/** Above-table controls: extra-column selector and the manual-row time entry popover. */
export function RoastProfileTableToolbar({
  rows,
  readOnly,
  extraColumns,
  onExtraColumnsChange,
  onAddManualRow,
}: RoastProfileTableToolbarProps) {
  const { t } = useLanguage();
  const [manualTimeMinutes, setManualTimeMinutes] = useState('');
  const [manualTimeSeconds, setManualTimeSeconds] = useState('');
  const [timeError, setTimeError] = useState('');
  const secondInputRef = useRef<HTMLInputElement>(null);

  const handleAddRow = (close?: () => void) => {
    const mm = parseInt(manualTimeMinutes || '0', 10);
    const ss = parseInt(manualTimeSeconds || '0', 10);

    if (isNaN(mm) || isNaN(ss) || mm < 0 || ss < 0 || ss >= 60) {
      if (ss >= 60) {
        setTimeError(t('roast.profileTable.errors.secondsRange'));
      } else {
        setTimeError(t('roast.profileTable.errors.invalidTime'));
      }
      return;
    }

    const totalSeconds = mm * 60 + ss;

    // Validation: must be within 30 seconds of the last row
    if (rows.length > 0) {
      const lastSeconds = Math.max(...rows.map((r) => r.seconds));
      const lastTimeStr = rows.find((r) => r.seconds === lastSeconds)?.time || '';

      if (totalSeconds <= lastSeconds) {
        setTimeError(t('roast.profileTable.errors.afterLastRow', { lastTime: lastTimeStr }));
        return;
      }

      if (totalSeconds >= lastSeconds + 30) {
        setTimeError(
          t('roast.profileTable.errors.within30s', {
            before: `${String(Math.floor((lastSeconds + 30) / 60)).padStart(2, '0')}:${String((lastSeconds + 30) % 60).padStart(2, '0')}`,
          })
        );
        return;
      }
    }

    const timeStr = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    onAddManualRow?.(timeStr);
    setManualTimeMinutes('');
    setManualTimeSeconds('');
    setTimeError('');
    close?.();
  };

  // The toolbar wraps rather than squeezing: the table often sits in a narrow grid column.
  return (
    <div className={`flex flex-wrap items-end justify-between gap-2 px-2 pt-2 mb-3 min-h-[60px] ${readOnly ? 'invisible' : ''}`}>
      <div className="flex flex-col">
        <p className="text-[10px] mb-1 font-bold text-gray-500 uppercase tracking-wider">{t('roast.profileTable.additionalColumn')}</p>
        <div className="w-40 sm:w-56">
          <Listbox value={extraColumns} onChange={onExtraColumnsChange} multiple>
            <div className="relative">
              <ListboxButton
                data-test-id="roast-extra-columns-selector"
                className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-[11px] transition-all"
              >
                <span className="block truncate">
                  {extraColumns.length === 0
                    ? t('roast.profileTable.noneSelected')
                    : extraColumns.length === 1
                      ? extraColumnOptions.find((o) => o.key === extraColumns[0])?.label
                      : t('roast.profileTable.selectedCount', { count: extraColumns.length })}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-3 w-3 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
              <Portal>
                <ListboxOptions
                  anchor="bottom start"
                  className="z-[100] mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-xs shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none [--anchor-gap:4px] animate-in fade-in slide-in-from-top-1"
                >
                  {extraColumnOptions.map((option) => (
                    <ListboxOption
                      key={option.key}
                      value={option.key}
                      data-test-id={`roast-extra-column-option-${option.key}`}
                      className={({ focus }: { focus: boolean }) =>
                        `relative cursor-default select-none py-1.5 pl-8 pr-3 transition-colors ${focus ? 'bg-primary-50 text-primary-700' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }: { selected: boolean }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{option.label}</span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-600">
                              <Check className="h-3 w-3" aria-hidden="true" strokeWidth={3} />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Portal>
            </div>
          </Listbox>
        </div>
      </div>

      {!readOnly && onAddManualRow && (
        <Popover className="relative">
          <PopoverButton
            className="flex items-center gap-1.5 whitespace-nowrap text-[11px] bg-white text-primary-600 hover:bg-primary-50 px-4 py-1.5 rounded-md font-bold transition-all border border-primary-200 shadow-sm hover:shadow active:scale-95 focus:outline-none"
            data-test-id="add-manual-row-btn"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            {t('roast.profileTable.addRow')}
          </PopoverButton>

          <Portal>
            <PopoverPanel
              anchor="bottom end"
              className="bg-white border border-gray-100 rounded-xl shadow-2xl p-4 z-[100] w-56 transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 [--anchor-gap:8px] ring-1 ring-black/5"
            >
              {({ close }) => (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-primary-50 rounded-lg text-primary-600">
                      <Plus className="h-3.5 w-3.5" />
                    </div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">{t('roast.profileTable.manualTimeEntry')}</p>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex flex-col gap-1 w-full">
                        <span className="text-[9px] text-gray-400 font-bold uppercase text-center">{t('roast.profileTable.min')}</span>
                        <input
                          autoFocus
                          type="text"
                          inputMode="numeric"
                          placeholder="00"
                          maxLength={2}
                          className="ios-no-autozoom-input w-full text-sm font-semibold border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-center bg-gray-50/50"
                          value={manualTimeMinutes}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, '');
                            setManualTimeMinutes(v);
                            setTimeError('');
                          }}
                          onBlur={(e) => {
                            const v = e.target.value;
                            if (v.length === 1) setManualTimeMinutes(`0${v}`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddRow(close);
                            }
                            if (e.key === ':' || e.key === 'Tab') {
                              e.preventDefault();
                              secondInputRef.current?.focus();
                            }
                          }}
                        />
                      </div>

                      <span className="text-gray-300 font-bold pt-4">:</span>

                      <div className="flex flex-col gap-1 w-full">
                        <span className="text-[9px] text-gray-400 font-bold uppercase text-center">{t('roast.profileTable.sec')}</span>
                        <input
                          ref={secondInputRef}
                          type="text"
                          inputMode="numeric"
                          placeholder="00"
                          maxLength={2}
                          className="ios-no-autozoom-input w-full text-sm font-semibold border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-center bg-gray-50/50"
                          value={manualTimeSeconds}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, '');
                            setManualTimeSeconds(v);
                            setTimeError('');
                          }}
                          onBlur={(e) => {
                            const v = e.target.value;
                            if (v.length === 1) setManualTimeSeconds(`0${v}`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddRow(close);
                            }
                          }}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAddRow(close)}
                      className="w-full py-2.5 bg-primary-600 text-white text-[11px] font-bold rounded-lg hover:bg-primary-700 transition-all shadow-md hover:shadow-lg active:scale-[0.98] outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      data-test-id="confirm-add-manual-row-btn"
                    >
                      {t('roast.profileTable.addToProfile')}
                    </button>
                  </div>

                  {timeError && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-md">
                      <p className="text-[10px] text-red-600 font-bold text-center">{timeError}</p>
                    </div>
                  )}
                </>
              )}
            </PopoverPanel>
          </Portal>
        </Popover>
      )}
    </div>
  );
}
